/** Planos de corte soltos, ao lado da caixa de corte.
 *
 *  A caixa de corte é seis planos presos uns aos outros: serve para
 *  isolar um pedaço do edifício, e não serve para nada mais. Quem quer
 *  um corte pela fachada, outro pelo piso e outro por uma parede
 *  inclinada, os três ao mesmo tempo, não tem por onde o fazer.
 *
 *  Estes são independentes: cada um tem a sua direcção e a sua posição,
 *  cria-se, desliga-se e tira-se sem mexer nos outros.
 *
 *  **Cortam em conjunto**, e isto é o que apanha toda a gente: o que
 *  fica visível é o que está do lado bom de TODOS os planos, não de
 *  algum. Três planos mal postos deixam o ecrã vazio sem se perceber
 *  porquê. Por isso o painel diz quantos estão ligados, e por isso o
 *  desligar de um é um clique e não um menu.
 *
 *  Convenção da direcção: a normal aponta para o lado que FICA. É a do
 *  three.js, que deita fora o que tiver distância negativa ao plano, e
 *  não se inverte aqui para não haver duas convenções na mesma casa.
 *
 *  Ficheiro puro, sem three.js: recebe e devolve números. O que corre
 *  no ecrã é o `useSectionBox`, que junta estes planos aos da caixa.
 */

export type Vec3 = readonly [number, number, number]

export interface PlanoDeCorte {
  id: string
  nome: string
  /** Unitária, e aponta para o lado que fica. */
  normal: Vec3
  /** Um ponto qualquer sobre o plano, em coordenadas do mundo. */
  ponto: Vec3
  activo: boolean
  /** Desenhar o plano (polígono, pegas e nome) no 3D. Escondido, o
   *  corte continua. Ausente = à vista. */
  mostrar?: boolean
  /** Travado: não se move, não roda e não se tira por engano (as pegas
   *  desaparecem e os campos ficam só de leitura). Ligar, desligar,
   *  esconder e mudar o nome continuam. */
  travado?: boolean
}

/** Uma combinação de planos com nome ("Corte A-A + Piso 1"): aplicá-la
 *  liga os planos dela e desliga os outros. */
export interface CombinacaoDeCorte {
  id: string
  nome: string
  /** Ids dos planos que a combinação liga. */
  planos: string[]
}

export type Eixo = 'x' | 'y' | 'z'

export function comprimento(v: Vec3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2])
}

/** Normaliza, e devolve null quando não há direcção nenhuma.
 *
 *  Um vector nulo não define um plano. Devolver (0,0,0) normalizado
 *  daria NaN e o corte desaparecia sem erro nenhum. */
export function normalizar(v: Vec3): Vec3 | null {
  const c = comprimento(v)
  if (!Number.isFinite(c) || c < 1e-9) return null
  return [v[0] / c, v[1] / c, v[2] / c]
}

export function produtoInterno(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

/** Zero é zero, e não menos zero.
 *
 *  O JavaScript tem `-0`, e ele aparece sozinho mal se nega alguma
 *  coisa que valia zero. É igual a `0` numa comparação, mas escreve-se
 *  "-0" no ecrã e grava-se "-0" no JSON, e isso faz uma pessoa pensar
 *  que há aqui um defeito. */
function semZeroNegativo(n: number): number {
  return n === 0 ? 0 : n
}

/**
 * A constante do plano na forma que o three.js quer.
 *
 * O three.js guarda `normal` e `constant`, com a superfície em
 * `dot(normal, p) + constant = 0`, e deita fora o que ficar negativo.
 */
export function constanteDe(plano: PlanoDeCorte): number {
  return semZeroNegativo(-produtoInterno(plano.normal, plano.ponto))
}

/** Move o plano ao longo da sua normal. Positivo tira mais. */
export function deslocar(plano: PlanoDeCorte, metros: number): PlanoDeCorte {
  return {
    ...plano,
    ponto: [
      plano.ponto[0] + plano.normal[0] * metros,
      plano.ponto[1] + plano.normal[1] * metros,
      plano.ponto[2] + plano.normal[2] * metros,
    ],
  }
}

/**
 * Troca o lado que fica.
 *
 * O plano é o mesmo, no mesmo sítio: o que muda é de que lado se corta.
 * É a operação que toda a gente procura logo a seguir a criar um corte,
 * porque a metade que aparece é metade das vezes a errada.
 */
export function inverter(plano: PlanoDeCorte): PlanoDeCorte {
  return {
    ...plano,
    normal: [
      semZeroNegativo(-plano.normal[0]),
      semZeroNegativo(-plano.normal[1]),
      semZeroNegativo(-plano.normal[2]),
    ],
  }
}

const NORMAIS: Record<Eixo, Vec3> = {
  x: [1, 0, 0],
  y: [0, 1, 0],
  z: [0, 0, 1],
}

/** Rótulo do eixo para o utilizador, na convenção do IFC (Z vertical,
 *  Y para Norte).
 *
 *  O `Eixo` é o do mundo three (y para cima), e é nele que as normais
 *  são guardadas, nos planos e no que os viewpoints e o BCF levam. Por
 *  isso só o RÓTULO muda: o "+ Y" do three, que corta na horizontal,
 *  aparece como "Z", tal como no plano de trabalho "XY (planta)". */
export const ROTULO_EIXO: Record<Eixo, string> = {
  x: 'X',
  y: 'Z',
  z: 'Y',
}

/** Um plano perpendicular a um eixo, a passar por um ponto. */
export function doEixo(
  eixo: Eixo,
  ponto: Vec3,
  id: string,
  numero: number,
): PlanoDeCorte {
  return {
    id,
    nome: `Corte ${ROTULO_EIXO[eixo]}${numero > 1 ? ` ${numero}` : ''}`,
    normal: NORMAIS[eixo],
    ponto,
    activo: true,
  }
}

/**
 * Um plano a partir de uma face clicada.
 *
 * É a forma de "rodar graficamente" que serve para alguma coisa num
 * modelo: em vez de arrastar um manípulo até a inclinação parecer certa,
 * clica-se na parede e o plano fica exactamente paralelo a ela. Uma
 * fachada oblíqua não tem ângulo redondo nenhum, e apanhá-lo à mão é
 * trabalho perdido.
 *
 * Devolve null quando a normal não presta, que acontece quando o clique
 * apanhou geometria degenerada.
 */
export function daFace(
  normal: Vec3,
  ponto: Vec3,
  id: string,
  numero: number,
): PlanoDeCorte | null {
  const n = normalizar(normal)
  if (!n) return null
  return {
    id,
    nome: `Corte pela face${numero > 1 ? ` ${numero}` : ''}`,
    normal: n,
    ponto,
    activo: true,
  }
}

/** Quantos estão mesmo a cortar. */
export function activos(planos: readonly PlanoDeCorte[]): number {
  return planos.filter((p) => p.activo).length
}

/**
 * O aviso a mostrar, se houver algum.
 *
 * Cortar com muitos planos ao mesmo tempo quase nunca é o que se quer:
 * como cortam em conjunto, cada um tira mais, e a partir de três é
 * fácil ficar sem nada no ecrã e culpar a aplicação. Dizê-lo antes vale
 * mais do que explicá-lo depois.
 */
export function aviso(planos: readonly PlanoDeCorte[]): string | null {
  const n = activos(planos)
  if (n < 3) return null
  return (
    `${n} planos ligados ao mesmo tempo. Cortam em conjunto: só fica ` +
    'visível o que estiver do lado bom de todos. Se o ecrã ficar vazio, ' +
    'desliga alguns.'
  )
}

/** Os ângulos da normal, para se poder escrever um número em vez de
 *  apontar. Azimute a contar do +X no plano XZ, e elevação a partir
 *  desse plano. Graus, que é como as pessoas falam. */
export function angulosDe(normal: Vec3): { azimute: number; elevacao: number } {
  const n = normalizar(normal) ?? ([1, 0, 0] as Vec3)
  const grau = 180 / Math.PI
  return {
    azimute: Math.atan2(n[2], n[0]) * grau,
    elevacao: Math.asin(Math.max(-1, Math.min(1, n[1]))) * grau,
  }
}

/** O caminho inverso: dois ângulos em graus dão uma normal unitária. */
export function normalDosAngulos(azimute: number, elevacao: number): Vec3 {
  const rad = Math.PI / 180
  const a = azimute * rad
  const e = elevacao * rad
  const c = Math.cos(e)
  return [Math.cos(a) * c, Math.sin(e), Math.sin(a) * c]
}

/** O plano desenha-se no 3D? */
export function planoAVista(p: PlanoDeCorte): boolean {
  return p.mostrar !== false
}

/** O que se pode mudar num plano travado: tudo menos a posição e a
 *  direcção. Devolve a alteração já filtrada (vazia se nada sobra). */
export function alteracaoPermitida(
  plano: PlanoDeCorte,
  muda: Partial<PlanoDeCorte>,
): Partial<PlanoDeCorte> {
  if (!plano.travado) return muda
  const resto = { ...muda }
  delete resto.normal
  delete resto.ponto
  delete resto.id
  return resto
}

/** Os planos depois de aplicar uma combinação: ligados os dela,
 *  desligados os outros. Os ids que já não existem ignoram-se. */
export function aplicarCombinacao(
  planos: readonly PlanoDeCorte[],
  combinacao: CombinacaoDeCorte,
): PlanoDeCorte[] {
  const dela = new Set(combinacao.planos)
  return planos.map((p) => (p.activo === dela.has(p.id) ? p : { ...p, activo: dela.has(p.id) }))
}

/** A combinação é a que está agora no ecrã (os mesmos planos ligados)? */
export function combinacaoEmUso(
  planos: readonly PlanoDeCorte[],
  combinacao: CombinacaoDeCorte,
): boolean {
  const ligados = planos.filter((p) => p.activo).map((p) => p.id)
  const dela = combinacao.planos.filter((id) => planos.some((p) => p.id === id))
  return ligados.length > 0 && ligados.length === dela.length && ligados.every((id) => dela.includes(id))
}

/** O nome seguinte por omissão: "Combinação 1", "Combinação 2"... */
export function nomeDaCombinacao(existentes: readonly CombinacaoDeCorte[]): string {
  let n = existentes.length + 1
  const nomes = new Set(existentes.map((c) => c.nome))
  while (nomes.has(`Combinação ${n}`)) n++
  return `Combinação ${n}`
}
